<template>
  <v-layout row wrap class="dealFormHeader">
    <v-flex lg6 md6 xs6 xs12>
      <v-container grid-list-sm class="px-0 py-0 mx-0 my-0">
        <v-layout row wrap align-start fill-height>
          <v-flex lg3 md3 xs4>
            <vonap-form-label label="Product">
              <kendo-dropdownlist
                :data-source="products"
                :data-text-field="'productCode'"
                :data-value-field="'productId'"
                :disabled="!isEnabled"
                v-model="dealModel.productId"
                style="width: 100%"
                @databound="productDataBound"
              >
              </kendo-dropdownlist>
            </vonap-form-label>
          </v-flex>
          <v-flex lg3 md3 xs4>
            <vonap-form-label label="Trade Date">
              <kendo-datepicker
                name="TradeDate"
                style="width: 100%"
                :format="'yyyy-MM-dd'"
                required="required"
                :disabled="!isEnabled"
                :validation-message="'Trade Date is Required'"
                v-model="dealModel.tradeDate"
              ></kendo-datepicker>
            </vonap-form-label>
          </v-flex>
          <v-flex lg3 md3 xs4>
            <vonap-form-label label="Counterparty / Broker">
              <kendo-dropdownlist
                :data-source="counterpartyDataSource"
                :data-text-field="'counterpartyCode'"
                :data-value-field="'counterpartyId'"
                required="required"
                :disabled="!isEnabled"
                :validation-message="'Counterparty is Required'"
                v-model.number="dealModel.counterpartyId"
                @databound="counterpartyDataBound"
                @change="counterpartyChange"
                style="width: 100%"
              >
              </kendo-dropdownlist>
            </vonap-form-label>
          </v-flex>
          <v-flex lg3 md3 xs4>
            <vonap-form-label label="Division">
              <kendo-dropdownlist
                :data-source="divisionDataSource"
                :data-text-field="'divisionName'"
                :data-value-field="'divisionId'"
                :disabled="!isEnabled"
                v-model="dealModel.divisionId"
                @databound="dynamicDropDownDataBound"
                style="width: 100%"
                name="Division"
                required
              >
              </kendo-dropdownlist>
            </vonap-form-label>
          </v-flex>
          <v-flex lg3 md3 xs4>
            <vonap-form-label label="Reference Price">
              <kendo-numerictextbox
                v-model.number="dealModel.referenceRate"
                required="required"
                :validation-message="'Reference Rate is Required'"
                :disabled="!isEnabled"
                :decimals="16"
                :format="'n4'"
                :min="0"
                :round="false"
                :spinners="false"
                ref="referenceRate"
              >
              </kendo-numerictextbox>
            </vonap-form-label>
          </v-flex>
          <group-category-item
            v-for="category in clientCategories"
            :category="category"
            :key="category.groupCategoryId"
            :data-source="getItems(category.groupCategoryId)"
            :value="getCategoryValue(category.groupCategoryId)"
            :disabled="!isEnabled"
            @change="
              changeCategoryValue($event.groupCategoryId, $event.groupCategoryName, $event.value)
            "
          />
        </v-layout>
      </v-container>
    </v-flex>
    <v-flex md5 lg6 xs12>
      <vonap-form-label label="Comments">
        <textarea
          name="comments"
          style="width: 98%"
          :disabled="!isEnabled"
          rows="4"
          v-model="dealModel.comment"
        ></textarea>
      </vonap-form-label>
    </v-flex>
  </v-layout>
</template>

<script>
import axios from 'axios'
import VonapFormLabel from '@/components/VonapFormLabel'
import GroupCategoryItem from '@/components/GroupCategoryItem'
import {mapGetters} from 'vuex'
import {uniqueBy} from '@/helpers/array'
import {kendoNumericFocus} from '@/helpers/kendo-helpers'

export default {
  props: {
    dealId: {type: [Number, String], required: true},
    isEnabled: {type: Boolean, required: true},
    dealModel: {type: Object, required: true},
    dealTypeId: {type: Number, required: true},
    marketId: {type: Number, required: true},
  },
  async created() {
    const {data: products} = await axios.get('/api/list/deal/products', {
      params: {
        dealId: this.dealId,
        clientId: this.clientId,
        dealTypeId: this.dealTypeId,
        // Allow to enter any product for New trades with no dealType specified
        marketId:
          this.$router.currentRoute.name === 'list-base-order' && this.dealId == 0
            ? -1
            : this.marketId,
      },
    })

    this.products = products
  },
  mounted() {
    this.$refs.referenceRate.kendoWidget().element.bind('focus', kendoNumericFocus)
  },
  data() {
    return {
      products: [],
    }
  },
  methods: {
    getItems(categoryId) {
      const items = this.dealModel.clientGroupCategories
        .filter((item) => item.groupCategoryId === categoryId)
        .map((item) => ({value: item.groupItemId, text: item.name}))

      return [{text: 'None', value: ''}, ...items]
    },
    getCategoryValue(categoryId) {
      const value = this.dealModel.dealGroupingItems.find(
        (item) => item.groupCategoryId === categoryId,
      )

      if (value) {
        return value.groupItemId || value.textValue
      }
      return ''
    },
    counterpartyChange(e) {
      const dataItem = e.sender.dataItem()
      this.dealModel.futureContractCommission = dataItem.futureContractCommission
      if (dataItem != null) {
        this.dealModel.counterpartyId = dataItem.counterpartyId
        this.dealModel.counterpartyName = dataItem.counterpartyCode
      }
    },
    changeCategoryValue(groupCategoryId, groupCategoryName, value) {
      let groupItemId = value
      const item = this.dealModel.clientGroupCategories.find((x) => x.groupItemId === groupItemId)
      if (item == null) groupItemId = 0
      const other = this.dealModel.dealGroupingItems.filter(
        (x) => x.groupCategoryId !== groupCategoryId,
      )
      this.dealModel.dealGroupingItems = [
        ...other,
        ...(value === 0
          ? []
          : [
              {
                dealId: this.dealModel.dealId,
                groupItemId,
                groupCategoryId,
                textValue: !item ? value : null,
              },
            ]),
      ]
    },
    productDataBound(e) {
      if (!e.sender.value().length) {
        e.sender.value(this.dealModel.productId)
      }
    },
    counterpartyDataBound({sender}) {
      setTimeout(() => {
        sender.value(this.dealModel.counterpartyId)
      }, 1000)
    },
    dynamicDropDownDataBound({sender}) {
      const value = sender.value()
      if (Array.isArray(value) && value.length === 0) {
        sender.select(0)
        this.dealModel[sender.options.dataValueField] = sender.value()[0]
      }
    },
  },
  computed: {
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
    clientCategories() {
      return uniqueBy(this.dealModel.clientGroupCategories, 'groupCategoryId').map(
        ({groupCategoryId, groupCategoryName, groupCategoryTypeId}) => ({
          groupCategoryId,
          groupCategoryName,
          groupCategoryTypeId,
        }),
      )
    },
    selectedProduct() {
      return this.products.find((x) => x.productId == this.dealModel.productId)
    },
    counterpartyDataSource() {
      return new kendo.data.DataSource({
        transport: {
          read: {
            type: 'GET',
            dataType: 'json',
            url: '/api/list/deal/counterparties',
            data: {
              dealId: this.dealId,
              clientId: this.clientId,
              marketId: this.dealModel.marketId,
            },
          },
        },
      })
    },
    divisionDataSource() {
      return new kendo.data.DataSource({
        transport: {
          read: {
            type: 'GET',
            dataType: 'json',
            url: '/api/list/deal/divisions',
            data: {
              dealId: this.dealId,
              clientId: this.clientId,
              productId: this.dealModel.productId,
              marketId: this.dealModel.marketId,
            },
          },
        },
      })
    },
  },
  components: {
    VonapFormLabel,
    GroupCategoryItem,
  },
  watch: {
    selectedProduct(newValue) {
      this.$emit('productChange', newValue)
    },
  },
}
</script>
<style scoped>
.dealFormHeader .v-label,
.dealFormHeader .k-input,
.dealFormHeader span.k-widget.k-dropdown {
  font-size: 12px;
}
.dealFormHeader textarea {
  display: flex;
  vertical-align: bottom;
  border: 1px;
  border-style: dotted;
  background: #f6f6f6;
}

.dealFormHeader input[type='text'] {
  display: flex;
  vertical-align: bottom;
  border: 1px;
  border-style: dotted !important;
  background: #f6f6f6;
}
</style>
